import { supabase } from '@/lib/supabase'
import type { Coupon, CouponUpsert } from '../types'

function isUuid(v?: string | null): boolean {
  if (!v) return false
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)
}

export async function listCoupons(): Promise<(Coupon & { is_live?: boolean })[]> {
  // Prefer RPC admin (bypass RLS filters; sorted desc)
  try {
    const { data, error } = await supabase.rpc('admin_list_discount_coupons')
    if (error) throw error
    return (data || []) as any
  } catch {
    const { data, error } = await supabase
      .from('discount_coupons')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) throw error
    return (data || []) as any
  }
}

export async function upsertCoupon(payload: CouponUpsert): Promise<{ id: string }>{
  const body: any = { ...payload }
  if (!isUuid(body.id)) delete body.id
  const recRaw = (body.recurrence || '').toString().toLowerCase()
  body.recurrence = recRaw === 'recurring' ? 'recurring' : 'one_time'
  if (body.discount_type === 'percent') {
    body.percent_off = Math.max(1, Math.min(99, Math.floor(Number(body.percent_off || 0))))
    body.amount_off_cents = null
    body.currency = null
  } else {
    body.amount_off_cents = Math.max(0, Math.floor(Number(body.amount_off_cents || 0)))
    body.percent_off = null
    body.currency = (body.currency || 'BRL').toString().toUpperCase()
  }
  const { data, error } = await supabase.rpc('admin_upsert_discount_coupon', { p: body })
  if (error) throw error
  const id = (Array.isArray(data) ? (data as any)[0] : data) as string
  return { id }
}

export async function deleteCoupon(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_discount_coupon', { p_id: id })
  if (error) throw error
}

export type CouponValidation = {
  ok: boolean
  type?: 'percent' | 'fixed'
  percent_off?: number
  amount_off_cents?: number
  currency?: string
  name?: string
  reason?: string
}

export async function validateCoupon(code: string): Promise<CouponValidation> {
  const c = (code || '').trim()
  if (!c) return { ok: false, reason: 'Informe o código do cupom' }
  // 1) discount_coupons (módulo atual)
  try {
    const { data, error } = await supabase
      .from('discount_coupons')
      .select('name, discount_type, percent_off, amount_off_cents, currency')
      .ilike('code', c)
      .maybeSingle()
    if (error) return { ok: false, reason: error.message }
    if (data) {
      const row: any = data
      if (row.discount_type === 'percent') {
        const pct = Math.max(1, Math.min(99, Math.floor(Number(row.percent_off || 0))))
        return { ok: true, type: 'percent', percent_off: pct, name: row.name }
      }
      const cents = Math.max(0, Math.floor(Number(row.amount_off_cents || 0)))
      return { ok: true, type: 'fixed', amount_off_cents: cents, currency: (row.currency || 'BRL'), name: row.name }
    }
  } catch {}
  // 2) Compatibilidade: affiliate_coupons
  try {
    const { data: aff } = await supabase
      .from('affiliate_coupons')
      .select('mode,value,active')
      .ilike('code', c)
      .maybeSingle()
    if (aff && (aff as any).active !== false) {
      const row: any = aff
      const mode = String(row.mode || '').toLowerCase()
      if (mode === 'percent') {
        const pct = Math.max(1, Math.min(99, Math.floor(Number((row.value || 0) * 100))))
        return { ok: true, type: 'percent', percent_off: pct }
      }
      if (mode === 'fixed') {
        const cents = Math.max(0, Math.floor(Number((row.value || 0) * 100)))
        return { ok: true, type: 'fixed', amount_off_cents: cents, currency: 'BRL' }
      }
    }
  } catch {}
  return { ok: false, reason: 'Cupom inválido ou expirado' }
}

export type AffiliateBasic = { id: string; name: string | null; email: string | null; code: string | null }

export async function listAffiliatesBasic(): Promise<AffiliateBasic[]> {
  // Base: affiliates table
  const { data: affs } = await supabase.from('affiliates').select('user_id, code, active')
  const ids = Array.from(new Set((affs ?? []).map((a: any) => a.user_id)))
  let byId: Record<string, { full_name: string | null; email: string | null }> = {}
  if (ids.length > 0) {
    const { data: profs } = await supabase
      .from('v_profiles_contact_master')
      .select('id, full_name, email')
      .in('id', ids)
    for (const p of profs ?? []) {
      byId[(p as any).id] = { full_name: (p as any).full_name ?? null, email: (p as any).email ?? null }
    }
  }
  return (affs ?? []).map((a: any) => ({ id: a.user_id, code: a.code ?? null, name: byId[a.user_id]?.full_name ?? null, email: byId[a.user_id]?.email ?? null }))
}

export async function listCouponsByAffiliate(affiliateUserId: string): Promise<(Coupon & { is_live?: boolean })[]> {
  try {
    if (!affiliateUserId) return []
    const { data, error } = await supabase.rpc('affiliate_list_my_coupons')
    if (error) throw error
    return (data || []) as any
  } catch {
    const { data, error } = await supabase
      .from('discount_coupons')
      .select('*')
      .eq('affiliate_user_id', affiliateUserId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return (data || []) as any
  }
}

// Resolve o melhor cupom (código) para um afiliado a partir do referral code (affiliates.code)
// Critério: recurring > maior desconto (percentual > fixo; depois maior valor) > mais recente
export async function getBestCouponCodeForAffiliateReferral(referralCode: string): Promise<string | null> {
  try {
    const code = (referralCode || '').trim()
    if (!code) return null
    // 1) Tenta Edge Function (evita RLS e centraliza regra)
    try {
      const { data } = await supabase.functions.invoke('resolve-affiliate-coupon', { body: { ref: code } })
      const resp = (Array.isArray(data) ? (data as any)[0] : (data as any)) as any
      const best = (resp?.code || resp?.best || null) as (string | null)
      if (best && String(best).trim()) return String(best).trim()
    } catch {}
    // 2) Fallback direto (pode falhar por RLS em alguns ambientes)
    const { data: aff } = await supabase.from('affiliates').select('user_id, code, active').ilike('code', code).maybeSingle()
    const affiliateUserId = (aff as any)?.user_id as string | undefined
    if (!affiliateUserId) return null
    const now = Date.now()
    // Tenta discount_coupons (oficial)
    try {
      const { data: list } = await supabase
        .from('discount_coupons')
        .select('code, discount_type, percent_off, amount_off_cents, recurrence, start_at, end_at, is_active, created_at')
        .eq('affiliate_user_id', affiliateUserId)
        .order('created_at', { ascending: false })
        .limit(50)
      const items = (list || []).filter((r: any) => (r?.is_active !== false) && (!r?.start_at || new Date(r.start_at).getTime() <= now) && (!r?.end_at || new Date(r.end_at).getTime() >= now))
      if (items.length > 0) {
        items.sort((a:any,b:any)=>{ const ar=String(a?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; const br=String(b?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; if(ar!==br) return br-ar; const ap=(a?.discount_type==='percent')?Number(a?.percent_off||0):-1; const bp=(b?.discount_type==='percent')?Number(b?.percent_off||0):-1; if(ap!==bp) return bp-ap; const af=(a?.discount_type==='fixed')?Number(a?.amount_off_cents||0):-1; const bf=(b?.discount_type==='fixed')?Number(b?.amount_off_cents||0):-1; if(af!==bf) return bf-af; const at=new Date(a?.created_at||0).getTime()||0; const bt=new Date(b?.created_at||0).getTime()||0; return bt-at })
        const pick = items[0]
        if (pick?.code) return String(pick.code)
      }
    } catch {}
    // Fallback: affiliate_coupons (legado)
    try {
      const { data: list2 } = await supabase
        .from('affiliate_coupons')
        .select('code, mode, value, active, recurrence, created_at')
        .eq('affiliate_user_id', affiliateUserId)
        .order('created_at', { ascending: false })
        .limit(50)
      const items2 = (list2 || []).filter((r: any) => (r?.active !== false))
      if (items2.length > 0) {
        items2.sort((a:any,b:any)=>{ const ar=String(a?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; const br=String(b?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; if(ar!==br) return br-ar; const ap=(String(a?.mode||'').toLowerCase()==='percent')?Number((a?.value||0)*100):-1; const bp=(String(b?.mode||'').toLowerCase()==='percent')?Number((b?.value||0)*100):-1; if(ap!==bp) return bp-ap; const af=(String(a?.mode||'').toLowerCase()==='fixed')?Number((a?.value||0)*100):-1; const bf=(String(b?.mode||'').toLowerCase()==='fixed')?Number((b?.value||0)*100):-1; if(af!==bf) return bf-af; const at=new Date(a?.created_at||0).getTime()||0; const bt=new Date(b?.created_at||0).getTime()||0; return bt-at })
        const pick2 = items2[0]
        if (pick2?.code) return String(pick2.code)
      }
    } catch {}
    return null
  } catch { return null }
}
